import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function ChapterMgmtRow({ c, confirmDelete, onAskDelete, onConfirmDelete }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm" data-testid={`chapter-mgmt-row-${c.id}`}>
      <div>
        <span className="font-mono-spec text-xs text-[var(--muted)] mr-2">CH.{c.order}</span>
        {c.title}
        <span className="text-xs text-[var(--ink-soft)] ml-2">
          {c.start_date || c.end_date ? `· prévu ${c.start_date ? c.start_date.split("-").reverse().join("/") : "?"} → ${c.end_date ? c.end_date.split("-").reverse().join("/") : "?"}` : "· dates non définies"}
        </span>
        {c.started_at && <span className="text-xs text-[var(--ok)] ml-2">démarré</span>}
      </div>
      {!c.started_at && (
        confirmDelete ? (
          <button className="!py-0.5 !px-2 text-xs bg-[var(--late)] text-white" onClick={onConfirmDelete} data-testid={`confirm-delete-chapter-${c.id}`}>
            Confirmer ?
          </button>
        ) : (
          <button className="text-[var(--late)] hover:opacity-70" onClick={onAskDelete} data-testid={`delete-chapter-${c.id}`}>
            <Trash2 size={13} />
          </button>
        )
      )}
    </div>
  );
}

function ChapterAddForm({ assignmentId, form, setForm, onAdd }) {
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 pb-2">
      <div className="flex-1 min-w-[180px]">
        <label className="eyebrow block mb-1">Nouveau chapitre</label>
        <input className={inputCls} value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} placeholder="Titre du chapitre" data-testid={`chapter-title-input-${assignmentId}`} />
      </div>
      <div className="w-36">
        <label className="eyebrow block mb-1">Début prévu</label>
        <input className={inputCls} type="date" value={form.start_date} onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} data-testid={`chapter-start-input-${assignmentId}`} />
      </div>
      <div className="w-36">
        <label className="eyebrow block mb-1">Fin prévue</label>
        <input className={inputCls} type="date" value={form.end_date} onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))} data-testid={`chapter-end-input-${assignmentId}`} />
      </div>
      <button className="btn-primary !py-2.5 !px-4 text-sm flex items-center gap-1.5" onClick={onAdd} data-testid={`add-chapter-btn-${assignmentId}`}>
        <Plus size={14} /> Ajouter
      </button>
    </div>
  );
}

function useChaptersCrud(assignmentId, onChanged) {
  const [chapters, setChapters] = useState(null);
  const [form, setForm] = useState({ title: "", start_date: "", end_date: "" });
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/assignments/${assignmentId}`);
    setChapters(data.chapters);
  }, [assignmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!form.title.trim()) {
      toast.error("Titre du chapitre requis");
      return;
    }
    try {
      await api.post(`/admin/assignments/${assignmentId}/chapters`, {
        title: form.title,
        start_date: form.start_date || "",
        end_date: form.end_date || "",
      });
      setForm({ title: "", start_date: "", end_date: "" });
      toast.success("Chapitre ajouté");
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/chapters/${id}`);
      setConfirmId(null);
      toast.success("Chapitre supprimé");
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return { chapters, form, setForm, confirmId, setConfirmId, add, remove };
}

export default function ChaptersPanel({ assignmentId, onChanged }) {
  const { chapters, form, setForm, confirmId, setConfirmId, add, remove } = useChaptersCrud(assignmentId, onChanged);

  return (
    <div className="mb-4 ml-2 pl-4 border-l-2 border-[var(--parchment-2)]" data-testid={`chapters-panel-${assignmentId}`}>
      {!chapters ? (
        <div className="text-sm text-[var(--ink-soft)] py-2">Chargement...</div>
      ) : (
        <>
          <div className="space-y-1.5">
            {chapters.map((c) => (
              <ChapterMgmtRow
                key={c.id}
                c={c}
                confirmDelete={confirmId === c.id}
                onAskDelete={() => setConfirmId(c.id)}
                onConfirmDelete={() => remove(c.id)}
              />
            ))}
            {chapters.length === 0 && <div className="text-sm text-[var(--ink-soft)]">Aucun chapitre au programme.</div>}
          </div>
          <ChapterAddForm assignmentId={assignmentId} form={form} setForm={setForm} onAdd={add} />
        </>
      )}
    </div>
  );
}
